/**
 * MortgageeList — editable mortgagee list for the
 * ``carrier_mortgagee_update.before_submit`` HITL checkpoint.
 *
 * Lifted verbatim from ora's WorkflowDetailDrawer.tsx so both ora and the
 * Outlook plugin render the AM's review form identically. Consumed by
 * the shared ``HitlChoicePanel``, which seeds the list from
 * ``hitlQuestion.mortgagee_draft.mortgagees`` and always submits the
 * current state in the unwrapped ``{choice, mortgagee_draft: {mortgagees}}``
 * response payload.
 *
 * Controlled by the parent: every change emits the next array via
 * ``onChange``. ``disabled`` greys out every input but keeps the list
 * visible (used while a HITL submit is in flight).
 */

import { useRef } from "react";
import {
  ConfidenceChip,
  confidenceBorderStyle,
  type ConfidenceLevel,
} from "./confidenceChip";

export interface MortgageeRecord {
  name?: string;
  loan_number?: string;
  mailing_address?: string;
}

export interface MortgageeListProps {
  mortgagees: MortgageeRecord[];
  onChange: (updated: MortgageeRecord[]) => void;
  disabled?: boolean;
  /**
   * Per-field extraction confidence for the NEW mortgagee, keyed by record
   * field. Supplied by `HitlChoicePanel`, which maps the substrate's dotted
   * `mortgagees.<subKey>` addresses onto these three controls and rolls the
   * four address components up to their WEAKEST level — this surface shows one
   * `mailing_address` where the launch form shows street / city / state / zip.
   *
   * Per-LIST, not per-row: the substrate scores the mortgagee it extracted, and
   * a row the Servicer added by hand has no score to show. Optional, so a
   * workflow with no prefill renders no chips.
   *
   * The score belongs to the record that was SEEDED into this list, tracked by
   * identity (see `rowIds` below) — never to "whatever is at index 0".
   */
  fieldConfidence?: Partial<Record<keyof MortgageeRecord, ConfidenceLevel>>;
}

export function MortgageeList({
  mortgagees,
  onChange,
  disabled,
  fieldConfidence,
}: MortgageeListProps) {
  /**
   * Stable per-row identity, parallel to the controlled array.
   *
   * `MortgageeRecord` has no id of its own and must not grow one — the parent
   * submits this array verbatim as `mortgagee_draft.mortgagees`, so anything
   * added here ships to the substrate. The records are also replaced wholesale
   * on every keystroke (`{...m, [field]: value}`), so object identity is no
   * help either. Ids therefore live beside the list, and every mutation below
   * moves them with it.
   *
   * Array POSITION cannot carry identity here: the confidence chip describes
   * the mortgagee the substrate extracted and scored, and removing that record
   * shifts a hand-typed one into index 0, where the old `i === 0` test handed
   * it a chip and a coloured border it was never scored for — presenting a
   * typed value as machine-scored.
   */
  const idsRef = useRef<number[]>([]);
  const nextIdRef = useRef(0);
  const scoredIdRef = useRef<number | null>(null);
  const seededRef = useRef(false);

  // Reconcile with the controlled array. Every mutation below keeps the two in
  // lock-step, so this only absorbs an EXTERNAL change (a re-seed from another
  // gate): surplus rows take fresh ids, and the tail is trimmed.
  if (idsRef.current.length !== mortgagees.length) {
    const next = idsRef.current.slice(0, mortgagees.length);
    while (next.length < mortgagees.length) {
      next.push(nextIdRef.current);
      nextIdRef.current += 1;
    }
    idsRef.current = next;
  }
  // The seeded record is the scored one: `fieldConfidence` describes what the
  // substrate extracted, which is what the list first rendered with.
  if (!seededRef.current && mortgagees.length > 0) {
    seededRef.current = true;
    scoredIdRef.current = idsRef.current[0] ?? null;
  }
  const rowIds = idsRef.current;
  const scoredId = scoredIdRef.current;

  if (mortgagees.length === 0) return null;

  const update = (i: number, field: keyof MortgageeRecord, value: string) =>
    onChange(
      mortgagees.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)),
    );

  const remove = (i: number) => {
    idsRef.current = idsRef.current.filter((_, idx) => idx !== i);
    onChange(mortgagees.filter((_, idx) => idx !== i));
  };

  const add = () => {
    idsRef.current = [...idsRef.current, nextIdRef.current];
    nextIdRef.current += 1;
    onChange([
      ...mortgagees,
      { name: "", loan_number: "", mailing_address: "" },
    ]);
  };

  const inputClass =
    "w-full rounded border border-primary-gray-light-300 px-2 py-1 text-xs focus:outline-none focus:border-sunrise disabled:opacity-50 bg-base-white";
  const labelClass = "text-xs font-medium text-primary-gray-light-500";

  return (
    <div className="flex flex-col gap-2" data-testid="mortgagee-list">
      {mortgagees.map((m, i) => {
        const rowId = rowIds[i];
        // "Is this the record the substrate scored", by identity — NOT "is
        // this currently first".
        const scored = scoredId !== null && rowId === scoredId;
        return (
          <div
            key={rowId}
            data-testid={`mortgagee-row-${i}`}
            data-scored={scored ? "true" : "false"}
            className="rounded-xl border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-2 flex flex-col gap-1.5"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-primary-gray-light-500">
                Mortgagee {i + 1}
              </p>
              {mortgagees.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(i)}
                  disabled={disabled}
                  className="text-xs text-primary-error-600 hover:text-primary-error-800 disabled:opacity-40 leading-none"
                  aria-label={`Remove mortgagee ${i + 1}`}
                >
                  Remove
                </button>
              )}
            </div>
            <label className="flex flex-col gap-0.5">
              <span className={`${labelClass} flex items-center gap-1.5`}>
                Name
                {scored && <ConfidenceChip confidence={fieldConfidence?.name} />}
              </span>
              <input
                type="text"
                value={m.name ?? ""}
                onChange={(e) => update(i, "name", e.target.value)}
                disabled={disabled}
                className={inputClass}
                style={scored ? confidenceBorderStyle(fieldConfidence?.name) : undefined}
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className={`${labelClass} flex items-center gap-1.5`}>
                Loan #
                {scored && <ConfidenceChip confidence={fieldConfidence?.loan_number} />}
              </span>
              <input
                type="text"
                value={m.loan_number ?? ""}
                onChange={(e) => update(i, "loan_number", e.target.value)}
                disabled={disabled}
                className={inputClass}
                style={
                  scored ? confidenceBorderStyle(fieldConfidence?.loan_number) : undefined
                }
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className={`${labelClass} flex items-center gap-1.5`}>
                Mailing address
                {scored && <ConfidenceChip confidence={fieldConfidence?.mailing_address} />}
              </span>
              <textarea
                rows={3}
                value={m.mailing_address ?? ""}
                onChange={(e) => update(i, "mailing_address", e.target.value)}
                disabled={disabled}
                className={`${inputClass} resize-y`}
                style={
                  scored
                    ? confidenceBorderStyle(fieldConfidence?.mailing_address)
                    : undefined
                }
              />
            </label>
          </div>
        );
      })}
      <button
        type="button"
        onClick={add}
        disabled={disabled}
        className="text-xs text-primary-gray-light-500 hover:text-primary-gray-light-800 disabled:opacity-40 font-medium text-left"
      >
        + Add mortgagee
      </button>
    </div>
  );
}

export default MortgageeList;
